import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Blocco 3 · B · BackButton universale.
 *
 * Uso:
 *  1. Senza props → renderizzato una sola volta da <Shell>, si nasconde
 *     automaticamente sulle rotte root/pubbliche.
 *  2. Con `to="/qualcosa"` → link contestuale a una pagina specifica
 *     (usato dai componenti di dettaglio per un "Torna a X" mirato).
 *
 * Comportamento default (senza `to`):
 *  - navigate(-1) se `window.history.length > 1`
 *  - fallback a "/" (home) altrimenti
 */
/**
 * Root paths on which the back button is hidden.
 *
 * IMPORTANT: solo landing di sezione, NON le sotto-pagine.
 * Se ne aggiungi altre, aggiungi SOLO route di primo livello dai tab principali.
 * Esempi che NON vanno qui:
 *   - /cicli/aperti · /cicli/da-aprire · /cicli/chiusi  → back visibile
 *   - /tasks/history · /tasks/search · /tasks/matrix    → back visibile
 *   - /settings/notifiche · /settings/google-calendar   → back visibile
 */
const ROOT_PATHS = new Set([
  "/", "/home", "/agenda", "/tasks",
  "/cicli",
  "/mete", "/azioni",
  "/documenti",
  "/finances",
  "/prime-vision/crm",
  "/medico/formazione",
  "/settings", "/menu", "/chat", "/segretaria", "/notifications",
  "/deadlines", "/projects", "/life-areas",
]);
const PUBLIC_PATHS = new Set([
  "/login", "/register", "/verify-email", "/password-reset",
  "/password-reset/confirm", "/invitations/accept", "/privacy", "/terms",
  "/gdpr/cancel-deletion",
]);

export default function BackButton({ to, label = "Indietro", testid = "back-button", className = "", auto = false }) {
  const nav = useNavigate();
  const loc = useLocation();

  const cls = `inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition hover:bg-black/5 ${className}`.trim();
  const style = { borderColor: "var(--pxos-border)", color: "var(--pxos-text-dark)" };
  const inner = (<><ArrowLeft size={13} /> {label}</>);

  // Auto-hide mode (used by <Shell>): hide on root/public routes.
  if (auto) {
    const path = loc.pathname.replace(/\/+$/, "") || "/";
    if (ROOT_PATHS.has(path) || PUBLIC_PATHS.has(path)) return null;
  }

  if (to) return <Link to={to} className={cls} style={style} data-testid={testid}>{inner}</Link>;

  const goBack = () => {
    if (typeof window !== "undefined" && window.history && window.history.length > 1) {
      nav(-1);
    } else {
      nav("/");
    }
  };
  return (
    <button type="button" onClick={goBack} className={cls} style={style} data-testid={testid} aria-label="Torna indietro">
      {inner}
    </button>
  );
}
